import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { FC, useEffect, useState, useRef, Fragment } from 'react';
import debounce from 'lodash.debounce';
import { TableCell, TableRow } from '@mui/material';
import {
  ConfirmationModalContent,
  DateAndTime,
  Dropdown,
  InputField,
  LoadingSpinner,
  ModalPopup,
  Table,
  Tooltip
} from 'components';
import { HeadCell } from 'components/table/table';
import { ClickAwayListener } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  getClinicalEncounters,
  deleteClinicalEncounters,
  putClinicalEncounters,
  ClinicalEncountersItem
} from 'store/clinicalEncounters/clinicalEncountersActions';
import { resetClinicalEncountersList } from 'store/clinicalEncounters/clinicalEncountersSlice';

import style from './clinicalEncounters.module.scss';

const headCells: HeadCell[] = [
  {
    id: 'dateTime',
    label: 'Date and Time',
    sort: true,
    width: '15%'
  },
  {
    id: 'type',
    label: 'Type',
    sort: true,
    width: '14%'
  },
  {
    id: 'location',
    label: 'Location',
    sort: true,
    width: '14%'
  },
  {
    id: 'provider',
    label: 'Provider',
    sort: true,
    width: '14%'
  },
  {
    id: 'reason',
    label: 'Reason',
    sort: true,
    width: '16%'
  },
  {
    id: 'status',
    label: 'Status',
    sort: true,
    width: '13%'
  },
  {
    id: 'source',
    label: 'Source',
    sort: true,
    width: '14%'
  }
];

type FormikInputProps = {
  formik: FormikProps<any>;
  name: string;
  disabled?: boolean;
};

const FormikInput: FC<FormikInputProps> = ({ formik, name, disabled }) => {
  return (
    <TableCell>
      <InputField
        classnamesProps={style.inputField}
        id={name}
        name={name}
        type="text"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values[name] || ''}
        disabled={disabled}
      />
    </TableCell>
  );
};

const TableCellValue: FC<{ value?: string | null; maxWidth?: string }> = ({
  value,
  maxWidth = '120px'
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      const { scrollWidth, offsetWidth } = textRef.current;
      setIsTextTruncated(scrollWidth > offsetWidth);
    }
  }, [value]);

  return (
    <TableCell sx={{ maxWidth }}>
      <div ref={textRef}>
        {isTextTruncated ? (
          <Tooltip
            toolTipText={isTextTruncated ? value : ''}
            disableInteractive
            blackTooltip
            placement="bottom-start"
            enterDelay={300}
          >
            <span className={clsx(style.valueWrap, style.truncatedText)}>{value}</span>
          </Tooltip>
        ) : (
          <span className={style.valueWrap}>{value}</span>
        )}
      </div>
    </TableCell>
  );
};

interface Props {
  isDisabled: boolean;
}

const ClinicalEncountersTable: FC<Props> = ({ isDisabled }) => {
  const { clientId } = useParams();
  const dispatch = useAppDispatch();
  const { data: clientData } = useAppSelector((state) => state.clients.client);
  const canEditClinicalEncounters = clientData?.clinicalEncountersEditingAllowed;
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [isAutosaving, setIsAutosaving] = useState<boolean | null>(null);
  const [typesOpen, setTypesOpen] = useState(false);
  const [statusesOpen, setStatusesOpen] = useState(false);
  const [entered, setEntered] = useState<boolean>(false);
  const [selectedEncounter, setSelectedEncounter] = useState<ClinicalEncountersItem | null>(null);
  const {
    clinicalEncountersList,
    clinicalEncountersListLoading,
    clinicalEncountersTypes,
    clinicalEncountersStatuses
  } = useAppSelector((state) => state.clinicalEncounters);
  const { content, page, size, filters } = clinicalEncountersList;
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (clientId) {
      dispatch(
        getClinicalEncounters({
          clientId,
          page,
          size,
          sorts: null,
          filters: null
        })
      );
    }

    return () => {
      dispatch(resetClinicalEncountersList());
    };
  }, [clientId]);

  const handleTypesDropdownOpen = () => setTypesOpen(true);
  const handleTypesDropdownClose = () => setTypesOpen(false);
  const handleStatusesDropdownOpen = () => setStatusesOpen(true);
  const handleStatusesDropdownClose = () => setStatusesOpen(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...selectedEncounter,
      typeId: selectedEncounter?.typeId?.toString() ?? '',
      statusId: selectedEncounter?.statusId?.toString() ?? '',
      time: selectedEncounter?.timeOfEncounter
        ? dayjs(selectedEncounter?.timeOfEncounter, 'hh:mm aa').format('hh:mm aa')
        : null
    },
    validationSchema: Yup.object().shape({
      dateOfEncounter: Yup.string().nullable(),
      timeOfEncounter: Yup.string().nullable(),
      time: Yup.string().nullable(),
      typeId: Yup.number().nullable(),
      location: Yup.string().nullable(),
      provider: Yup.string().nullable(),
      reason: Yup.string().nullable(),
      statusId: Yup.number().nullable()
    }),
    onSubmit: (values) => {
      if (clientId && selectedEncounter?.id) {
        setIsAutosaving(true);
        const data: ClinicalEncountersItem = {
          ...values,
          typeId: values.typeId ? parseInt(values.typeId, 10) : undefined,
          statusId: values.statusId ? parseInt(values.statusId, 10) : undefined,
          dateOfEncounter:
            values.dateOfEncounter && values.dateOfEncounter !== 'Invalid Date'
              ? values.dateOfEncounter
              : null,
          timeOfEncounter:
            values.time && values.time !== 'Invalid Date'
              ? dayjs(values.time, 'HH:mm:ss').format('hh:mm A')
              : selectedEncounter?.timeOfEncounter
        };
        dispatch(putClinicalEncounters({ clientId, encounterId: selectedEncounter.id, data })).then(
          (res: any) => {
            if (!res.error) {
              setIsAutosaving(false);
              if (entered) {
                setEditMode(false);
                setSelectedEncounter(null);
                handleTypesDropdownClose();
                handleStatusesDropdownClose();
                setEntered(false);
              }
            }
          }
        );
      }
    }
  });

  const onCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedEncounter(null);
    setEntered(false);
  };

  const handleDelete = async () => {
    if (clientId && selectedEncounter?.id) {
      const res: any = await dispatch(
        deleteClinicalEncounters({ clientId, encounterId: selectedEncounter.id })
      );
      if (!res.error) {
        onCancelDelete();
      }
    }
  };

  const getTableData = (data: any) => {
    if (clientId) {
      dispatch(
        getClinicalEncounters({
          clientId,
          page,
          size,
          filters,
          ...data
        })
      );
    }
  };

  const debouncedAutosave = debounce(async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      await formik.submitForm();
    }
  }, 400);

  useEffect(() => {
    if (formik.dirty) {
      debouncedAutosave();
    }
    return () => {
      debouncedAutosave.cancel();
    };
  }, [formik.values]);

  const handleFieldChange = (field: string, value: any) => {
    formik.setFieldTouched(field);
    formik.setFieldValue(field, value);
  };

  return (
    <>
      <Table
        ref={tableRef}
        tableWrapClassName={clsx(style.tableWrap, isDisabled && style.tableDisabled)}
        totalNumber={0}
        page={0}
        size={size}
        isLoading={clinicalEncountersListLoading}
        headCells={headCells}
        getTableData={getTableData}
        // TODO??
        // handleOnSort={(_sortConfig: any) => dispatch(updateSortsMetricList(_sortConfig))}
        showEmptyHeader={true}
        columns={content.map((row) => (
          <Fragment key={row.id}>
            <ClickAwayListener
              onClickAway={(event) => {
                if (editMode && selectedEncounter?.id === row.id) {
                  const target = event.target as Element;
                  if (target.closest('[data-test-id="clinical-encounters-form-table-warning"]')) {
                    return;
                  }
                  if (
                    !target.closest(`.${style.editMode}`) ||
                    !target.closest(`.${style.inputField}`) ||
                    !target.closest(`.${style.dropdownBox}`)
                  ) {
                    setEditMode(false);
                    setSelectedEncounter(null);
                    handleTypesDropdownClose();
                    handleStatusesDropdownClose();
                  }
                }
              }}
            >
              <TableRow
                data-test-id="clinical-encounters-form-table"
                id={`row-${row.id}`}
                onDoubleClick={() => {
                  if (isDisabled || !canEditClinicalEncounters) return;
                  setEditMode(true);
                  setSelectedEncounter(row);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') {
                    setEntered(false);
                  }
                  if (e.key === 'Enter') {
                    setEntered(true);
                    formik.handleSubmit();
                  }
                }}
              >
                {editMode && selectedEncounter?.id === row.id ? (
                  <>
                    <TableCell sx={{ maxWidth: '120px' }}>
                      <DateAndTime
                        dateValue={formik.values.dateOfEncounter || null}
                        datePickerStyles={style.inputField}
                        onChangeDate={(date: string) => {
                          formik.setFieldValue('dateOfEncounter', date).then(() => {
                            formik.setFieldTouched('dateOfEncounter', true);
                          });
                        }}
                        onBlurDate={() => formik.setFieldTouched('dateOfEncounter', true)}
                        timeValue={
                          formik.values.time && formik.values.time !== 'Invalid Date'
                            ? formik.values.time
                            : formik.values.timeOfEncounter
                            ? dayjs(formik.values.timeOfEncounter, 'hh:mm A').format('hh:mm:ss')
                            : null
                        }
                        timePickerStyles={style.inputField}
                        onChangeTime={(time: string) => {
                          if (time === 'Invalid Date') {
                            formik.setFieldValue('time', null).then(() => {
                              formik.setFieldTouched('time', true);
                            });
                          } else {
                            formik.setFieldValue('time', time).then(() => {
                              formik.setFieldTouched('time', true);
                            });
                          }
                        }}
                        onBlurTime={() => formik.setFieldTouched('time')}
                      />
                    </TableCell>
                    <TableCell>
                      <div className={style.dropdownBox}>
                        <div onClick={handleTypesDropdownOpen} className={style.dropdownOverBox} />
                        <Dropdown
                          className={style.dropdown}
                          onChange={(event) => {
                            handleFieldChange('typeId', event.target.value);
                            handleTypesDropdownClose();
                          }}
                          getOpen={(e) => {
                            if (!e && typesOpen) {
                              handleTypesDropdownClose();
                            }
                          }}
                          onBlur={formik.handleBlur}
                          open={typesOpen}
                          data={clinicalEncountersTypes}
                          label="Select"
                          name="typeId"
                          id="typeId"
                          value={formik.values.typeId?.toString() ?? ''}
                          fitWidthMore={50}
                          fitWidth
                        />
                      </div>
                    </TableCell>
                    <FormikInput formik={formik} name="location" />
                    <FormikInput formik={formik} name="provider" />
                    <FormikInput formik={formik} name="reason" />
                    <TableCell>
                      <div className={style.dropdownBox}>
                        <div
                          onClick={handleStatusesDropdownOpen}
                          className={style.dropdownOverBox}
                        />
                        <Dropdown
                          className={style.dropdown}
                          onChange={(event) => {
                            handleFieldChange('statusId', event.target.value);
                            handleStatusesDropdownClose();
                          }}
                          getOpen={(e) => {
                            if (!e && statusesOpen) {
                              handleStatusesDropdownClose();
                            }
                          }}
                          onBlur={formik.handleBlur}
                          open={statusesOpen}
                          data={clinicalEncountersStatuses}
                          label="Select"
                          name="statusId"
                          id="statusId"
                          value={formik.values.statusId?.toString() ?? ''}
                        />
                      </div>
                    </TableCell>
                    <FormikInput formik={formik} name="source" disabled />
                  </>
                ) : (
                  <>
                    <TableCellValue
                      value={`${
                        row.dateOfEncounter ? dayjs(row.dateOfEncounter).format('MM/DD/YY') : ''
                      } ${row.timeOfEncounter ?? ''}`}
                    />
                    <TableCellValue value={row.type} />
                    <TableCellValue value={row.location} />
                    <TableCellValue value={row.provider} />
                    <TableCellValue value={row.reason} maxWidth="140px" />
                    <TableCellValue value={row.status} />
                    <TableCell>
                      <div className={style.deleteWrap}>
                        <span>{row.source}</span>
                        <span
                          data-disabled={!canEditClinicalEncounters}
                          onClick={() => {
                            setDeleteModalOpen(true);
                            setSelectedEncounter(row);
                          }}
                          className={clsx('material-icons', style.icon)}
                        >
                          delete
                        </span>
                      </div>
                    </TableCell>
                  </>
                )}
              </TableRow>
            </ClickAwayListener>
          </Fragment>
        ))}
      />
      {editMode &&
        Object.keys(formik.errors).length === 0 &&
        Object.keys(formik.touched).length !== 0 &&
        isAutosaving !== null && (
          <div className={style.autosavedBox}>
            {isAutosaving ? (
              <LoadingSpinner size={20} />
            ) : (
              <>
                <span className="material-icons">check</span>Autosaved a few seconds ago
              </>
            )}
          </div>
        )}
      {Object.keys(formik.errors).length !== 0 &&
        Object.keys(formik.touched).length !== 0 &&
        editMode && (
          <div className={clsx(style.autosavedBox, style.autosavedError)}>
            <span className="material-icons-round">warning_amber</span>Unable to autosave. There are
            invalid values.
          </div>
        )}

      {!clinicalEncountersListLoading && content.length === 0 && (
        <div className={style.noData}>
          <p>Nothing to see here!</p>
        </div>
      )}
      <ModalPopup
        open={deleteModalOpen}
        customClassInsideContainer={style.modalDelete}
        handleClose={onCancelDelete}
      >
        <ConfirmationModalContent
          confirmButtonText="Delete"
          rejectButtonText="Cancel"
          title="Are you sure you want to delete this encounter?"
          text=" "
          handleConfirm={handleDelete}
          handleReject={onCancelDelete}
        />
      </ModalPopup>
    </>
  );
};

export default ClinicalEncountersTable;
